<template>
  <div class="adm">
    <div v-if="navOpen" class="adm-scrim" @click="navOpen = false"></div>

    <aside class="adm-side" :class="{ 'is-open': navOpen }" aria-label="Admin navigation">
      <div class="adm-side__brand">
        <img src="@/assets/img/logo.png" alt="YourDoc" />
        <span class="adm-side__tag">Admin</span>
      </div>

      <nav class="adm-nav">
        <template v-for="group in navGroups" :key="group.label">
          <div class="adm-nav__group">{{ group.label }}</div>
          <button v-for="item in group.items" :key="item.id" type="button" class="adm-nav__item"
                  :class="{ 'is-active': panel === item.id }" :aria-current="panel === item.id ? 'page' : null"
                  @click="navigate(item.id)">
            <i :class="item.icon"></i>
            {{ item.label }}
            <span v-if="item.id === 'doctors' && stats.pending" class="adm-nav__badge" :title="`${stats.pending} pending`">{{ stats.pending }}</span>
          </button>
        </template>
      </nav>

      <div class="adm-side__user">
        <span class="adm-avatar adm-avatar--sm adm-tone-violet">{{ initials(currentUser.username) }}</span>
        <div class="adm-side__user-text">
          <strong>Signed in</strong>
          <span :title="currentUser.username">{{ currentUser.username || "…" }}</span>
        </div>
        <button type="button" class="adm-icon-btn" title="Sign out" aria-label="Sign out" @click="confirmLogout = true">
          <i class="fas fa-sign-out-alt"></i>
        </button>
      </div>
    </aside>

    <div class="adm-main">
      <header class="adm-topbar">
        <button type="button" class="adm-icon-btn adm-menu-btn" aria-label="Open menu" @click="navOpen = true">
          <i class="fas fa-bars"></i>
        </button>
        <div>
          <h1 class="adm-topbar__title">{{ current.title }}</h1>
          <p class="adm-topbar__subtitle">{{ current.subtitle }}</p>
        </div>
        <div class="adm-topbar__actions">
          <a href="/" class="adm-btn adm-btn--ghost adm-btn--sm" target="_blank" rel="noopener">
            <i class="fas fa-external-link-alt"></i> View site
          </a>
        </div>
      </header>

      <main class="adm-content">
        <component :is="current.component" :key="panelKey"
                   :stats="stats" :stats-loaded="statsLoaded" :cities="panel === 'cities' ? cities : activeCities"
                   :loading="citiesLoading" :intent="intent" :current-user-id="currentUser.userId"
                   @notify="notify" @changed="refreshStats" @navigate="navigate" @cities-changed="refreshCities" />
      </main>
    </div>

    <div class="adm-toasts" aria-live="polite">
      <div v-for="toast in toasts" :key="toast.id" class="adm-toast" :class="`adm-toast--${toast.type}`" role="status">
        <i class="adm-toast__icon fas" :class="toast.type === 'error' ? 'fa-exclamation-circle' : 'fa-check-circle'"></i>
        <span>{{ toast.message }}</span>
        <button type="button" class="adm-toast__close" aria-label="Dismiss" @click="dismiss(toast.id)">
          <i class="fas fa-times"></i>
        </button>
      </div>
    </div>

    <admin-modal v-if="confirmLogout" size="sm" title="Sign out?" @close="confirmLogout = false">
      <p class="adm-logout-text">You'll need your email and password to sign in again.</p>
      <template #footer>
        <button type="button" class="adm-btn" @click="confirmLogout = false">Cancel</button>
        <button type="button" class="adm-btn adm-btn--primary" @click="logout">Sign out</button>
      </template>
    </admin-modal>
  </div>
</template>

<script>
import axios from "axios";
import { getCurrentUser, signOut } from "@/services/auth";
import "./admin.css";
import { errorMessage, initials } from "./admin-utils";
import AdminModal from "./components/AdminModal.vue";
import OverviewPanel from "./components/OverviewPanel.vue";
import DoctorsPanel from "./components/DoctorsPanel.vue";
import ClinicsPanel from "./components/ClinicsPanel.vue";
import ClinicUsersPanel from "./components/ClinicUsersPanel.vue";
import AdminsPanel from "./components/AdminsPanel.vue";
import SlotsPanel from "./components/SlotsPanel.vue";
import CitiesPanel from "./components/CitiesPanel.vue";

axios.defaults.baseURL = process.env.VUE_APP_API_URL;

const PANELS = {
  overview: { component: "OverviewPanel", title: "Overview", subtitle: "What needs your attention today." },
  doctors: { component: "DoctorsPanel", title: "Doctors", subtitle: "Review registrations and manage doctor accounts." },
  clinics: { component: "ClinicsPanel", title: "Clinics", subtitle: "Clinics and the doctors who practise at them." },
  "clinic-users": { component: "ClinicUsersPanel", title: "Clinic logins", subtitle: "Front-desk accounts that manage a clinic's appointments." },
  admins: { component: "AdminsPanel", title: "Admins", subtitle: "People who can use this console." },
  slots: { component: "SlotsPanel", title: "Slots", subtitle: "Turn doctors' weekly schedules into bookable slots." },
  cities: { component: "CitiesPanel", title: "Cities", subtitle: "Cities patients can search and book in." },
};

const NAV_GROUPS = [
  { label: "Dashboard", items: [{ id: "overview", label: "Overview", icon: "fas fa-chart-pie" }] },
  {
    label: "Manage",
    items: [
      { id: "doctors", label: "Doctors", icon: "fas fa-user-md" },
      { id: "clinics", label: "Clinics", icon: "fas fa-hospital" },
      { id: "slots", label: "Slots", icon: "fas fa-calendar-alt" },
      { id: "cities", label: "Cities", icon: "fas fa-city" },
    ],
  },
  {
    label: "Access",
    items: [
      { id: "clinic-users", label: "Clinic logins", icon: "fas fa-id-badge" },
      { id: "admins", label: "Admins", icon: "fas fa-user-shield" },
    ],
  },
];

const PANEL_KEY = "yourdoc.admin.panel";

export default {
  name: "AdminDashboard",
  components: { AdminModal, OverviewPanel, DoctorsPanel, ClinicsPanel, ClinicUsersPanel, AdminsPanel, SlotsPanel, CitiesPanel },
  data() {
    let panel = "overview";
    try {
      const saved = sessionStorage.getItem(PANEL_KEY);
      if (saved && PANELS[saved]) panel = saved;
    } catch (e) {
      // storage blocked: start on the overview
    }
    return {
      panel,
      panelKey: 0,
      intent: null,
      navOpen: false,
      navGroups: NAV_GROUPS,
      stats: { total: 0, active: 0, pending: 0 },
      statsLoaded: false,
      cities: [],
      citiesLoading: false,
      currentUser: { username: "", userId: "" },
      toasts: [],
      confirmLogout: false,
    };
  },
  computed: {
    current() {
      return PANELS[this.panel];
    },
    activeCities() {
      return this.cities.filter(c => c.is_active);
    },
  },
  methods: {
    initials,
    navigate(panel, intent = null) {
      if (!PANELS[panel]) return;
      this.panel = panel;
      this.intent = intent;
      this.panelKey += 1; // fresh panel, so the intent (filter / open form) applies
      this.navOpen = false;
      try {
        sessionStorage.setItem(PANEL_KEY, panel);
      } catch (e) {
        // not remembered
      }
      window.scrollTo({ top: 0 });
    },
    notify(message, type = "success") {
      const id = Date.now() + Math.random();
      this.toasts.push({ id, message, type });
      setTimeout(() => this.dismiss(id), type === "error" ? 8000 : 5000);
    },
    dismiss(id) {
      this.toasts = this.toasts.filter(t => t.id !== id);
    },
    async refreshStats() {
      try {
        const { data } = await axios.get("/doctors/stats");
        this.stats = data;
        this.statsLoaded = true;
      } catch (error) {
        this.notify(errorMessage(error, "Could not load doctor numbers."), "error");
      }
    },
    async refreshCities() {
      this.citiesLoading = true;
      try {
        const { data } = await axios.get("/cities/admin");
        this.cities = data || [];
        // Keep the booking dropdowns in step.
        this.$store.dispatch("loadCities", { force: true });
      } catch (error) {
        this.notify(errorMessage(error, "Could not load cities."), "error");
      } finally {
        this.citiesLoading = false;
      }
    },
    async logout() {
      this.confirmLogout = false;
      try {
        await signOut();
      } finally {
        ["useridpid", "usertype", "userdbid"].forEach(key => sessionStorage.removeItem(key));
        localStorage.removeItem("adminEmail");
        localStorage.removeItem("adminLoggedIn");
        this.$router.push("/admin-login");
      }
    },
  },
  mounted() {
    document.body.classList.add("adm-page");
  },
  beforeUnmount() {
    document.body.classList.remove("adm-page");
  },
  async created() {
    this.refreshStats();
    this.refreshCities();
    try {
      this.currentUser = await getCurrentUser();
    } catch (error) {
      // the router guard sends signed-out users to the login page
    }
  },
};
</script>

<style scoped>
.adm-logout-text {
  margin: 0;
}
</style>
